import React from "react";
import { Box } from "@mui/system";
import { Button, CircularProgress } from "@mui/joy";
import DarkInput from "../common/DarkInput";
import DarkTypography from "../common/DarkTypography";
import FooterBrand from "../common/FooterBrand";
import { useAuth } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import type { LoginForm } from "../../api/auth/Auth.typed";
import { useNavigate } from "react-router-dom";
import { authCardDarkInputSx, colorTokens, withAlpha } from "../../styles/globalStyles";
import { useZekayAuthMark } from "../../utils/useZekayAuthMark";

export default function ResetRequestPage() {
  const { src: zekayIconSrc, onImgError: onZekayIconError } = useZekayAuthMark();
  const [email, setEmail] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { resetRequest } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const handleReset = async () => {
    if (!email) {
      showAlert("Veuillez entrer une adresse e-mail valide.", "warning");
      return;
    }

    setIsLoading(true);
    try {
      const requestData: LoginForm = { Email: email };
      await resetRequest(requestData);
      showAlert("Email de réinitialisation envoyé !", "success");
      navigate("/");
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        overflowX: "hidden",
        padding: 2,
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            textAlign: "center",
            backgroundColor: "background.body",
            padding: 3,
            borderRadius: 2,
            boxShadow: `0 4px 12px ${withAlpha(colorTokens.bg.overlay, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 3 }}>
            <img src={zekayIconSrc} width="84px" alt="Zekay icon" onError={onZekayIconError} />
          </Box>

          <DarkTypography level="h4" sx={{ marginBottom: 2 }}>
            Réinitialisez votre mot de passe
          </DarkTypography>
          <DarkInput
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
            sx={authCardDarkInputSx}
            onKeyDown={(e) => e.key === "Enter" && handleReset()}
          />
          <Button
            variant="plain"
            size="sm"
            onClick={() => navigate("/")}
            sx={{ fontSize: "0.8rem", marginY: 1 }}
          >
            Retour à la connexion
          </Button>
          <Button
            variant="solid"
            color="primary"
            sx={{ marginTop: 2, width: "100%" }}
            onClick={handleReset}
            disabled={isLoading || !email}
          >
            {isLoading ? <CircularProgress size="sm" /> : "Envoyer"}
          </Button>
        </Box>
      </Box>
      <FooterBrand showChromeNote />
    </Box>
  );
}
